import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { tileService, accountService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";
import Select from "react-select";

const TileCreate = () => {
  const navigate = useNavigate();
  const { product_id } = useParams();
  const [productId, setProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPdfUpload, setShowPdfUpload] = useState(false);
  const [types, setTypes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState([]);
  const [fileName, setFileName] = useState("");
  const [defaultFileName, setDefaultFileName] = useState("");
  const [formData, setFormData] = useState({
    manufacturing_company_name: "",
    description: "",
    tile_type: "",
    tile_size: "",
    tile_grade: "",
    box_weight_kg: "",
    thickness_mm: "",
    tile_body: "",
    box: "",
    pcs_per_box: "",
    working_sqft_per_box: "",
    loading_address: "",
    hidden_district_ids: [],
    hidden_district_names: [],
  });

  const GRADE_CHOICES = [
    { value: "premium", label: "Premium" },
    { value: "otp", label: "One Time Premium" },
    { value: "std_ii", label: "STD-II" },
    { value: "com_iii", label: "Com-III" },
    { value: "eco_iv", label: "Reject/DD (Eco-IV)" },
    { value: "mix", label: "Mix" },
  ];

  const fetchTypes = async () => {
    try {
      setTypesLoading(true);
      const response = await tileService.getTypes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setTypes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching types:", error);
      toast.error("Failed to load tile types. Please refresh the page.");
    } finally {
      setTypesLoading(false);
    }
  };

  const fetchSizes = async () => {
    try {
      setSizesLoading(true);
      const response = await tileService.getSizes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setSizes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
      toast.error("Failed to load tile sizes. Please refresh the page.");
    } finally {
      setSizesLoading(false);
    }
  };

  const fetchDistricts = async () => {
    // Always start with loading state
    setDistrictsLoading(true);
    
    // Check if districts are already cached in localStorage
    const cachedDistricts = localStorage.getItem('districts');
    const cacheTimestamp = localStorage.getItem('districts_timestamp');
    const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    // If cached data exists and is not expired, use it
    if (cachedDistricts && cacheTimestamp) {
      const now = Date.now();
      const cacheAge = now - parseInt(cacheTimestamp);
      
      if (cacheAge < CACHE_DURATION) {
        try {
          const parsedDistricts = JSON.parse(cachedDistricts);
          setDistricts(parsedDistricts);
          setDistrictsLoading(false);
          return;
        } catch (error) {
          console.error("Error parsing cached districts:", error);
          // If parsing fails, continue to fetch from API
        }
      }
    }
    
    // If no cache or cache expired, fetch from API
    try {
      const response = await accountService.getAllDistricts();
      if (response && response.length) {
        let mapDistricts = response.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setDistricts(mapDistricts);
        
        // Cache the districts data
        localStorage.setItem('districts', JSON.stringify(mapDistricts));
        localStorage.setItem('districts_timestamp', Date.now().toString());
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Failed to load districts. Please refresh the page.");
    } finally {
      setDistrictsLoading(false);
    }
  };

  const fetchTileDetails = async () => {
    try {
      setDetailsLoading(true);
      const response = await tileService.getDetail(product_id); // API call works
      if (!response) {
        toast.error("Tile not found");
        return;
      }
      
      // Ensure districts are loaded before setting form data
      if (districts.length === 0) {
        await fetchDistricts();
      }
      
      // Handle both old format (separate IDs/names) and new format (single array)
      const hiddenDistrictIds = response.hidden_districts || response.hidden_district_ids || [];
      const hiddenDistrictNames = response.hidden_district_names || [];
      
      setFormData({
        manufacturing_company_name: response.manufacturing_company_name || response.name || "",
        description: response.description || "",
        tile_type: response.tile_type || "",
        tile_size: response.tile_size || "",
        tile_grade: response.tile_grade || "",
        box_weight_kg: response.box_weight_kg || "",
        thickness_mm: response.thickness_mm || "",
        tile_body: response.tile_body || "",
        box: response.box || "",
        pcs_per_box: response.pcs_per_box || "",
        working_sqft_per_box: response.working_sqft_per_box || "",
        loading_address: response.loading_address || "",
        hidden_district_ids: hiddenDistrictIds,
        hidden_district_names: hiddenDistrictNames,
      });
    } catch (error) {
      console.error("Error fetching tile:", error);
      toast.error("Failed to load tile data");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const productData = {
        manufacturing_company_name: formData.manufacturing_company_name,
        description: formData.description,
        category: 1,
        tile_type: parseInt(formData.tile_type),
        tile_grade: formData.tile_grade || "",
        tile_size: parseInt(formData.tile_size),
        box_weight_kg: parseFloat(formData.box_weight_kg) || 0,
        thickness_mm: parseFloat(formData.thickness_mm) || 0,
        tile_body: formData.tile_body || "",
        box: formData.box || "",
        pcs_per_box: parseInt(formData.pcs_per_box) || 0,
        working_sqft_per_box: parseFloat(formData.working_sqft_per_box) || 0,
        loading_address: formData.loading_address || "",
        hidden_districts: Array.isArray(formData.hidden_district_ids) ? formData.hidden_district_ids : [],
      };
      
      if (product_id) {
        await tileService.update(product_id, productData);
        setProductId(product_id);
        toast.success("Tile updated successfully!");
        navigate("/products/tiles");
      } else {
        let response = await tileService.create(productData);
        navigate(`/products/tiles/${response.product_id}/pdfs`);
      }
    } catch (error) {
      let message = "Failed to save product. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.non_field_errors.length > 0
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message);
      console.error("Error submitting tile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    setSelectedFile(files);
  };

  const saveUpload = async () => {
    try {
      setUploading(true);
      const promises = Array.from(selectedFile).map((element) => {
        return handleUpload(element);
      });
      await Promise.all(promises);
      setUploading(false);
      navigate("/products/tiles");
      toast.success("Tile created successfully!");
    } catch (error) {
      console.error("Error fetching data:", error);
      setUploading(false);
      toast.error("Failed to create tile");
    }
  };

  const handleUpload = async (file) => {
    if (!file) {
      toast.error("Please select a PDF file to upload");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", defaultFileName + " " + fileName + ".pdf");
      await tileService.uploadPdf(productId, formData);
      return true;
    } catch (error) {
      console.error("Error uploading PDF:", error);
      toast.error("Failed to upload PDF");
      return false;
    }
  };
  
  useEffect(() => {
    const initializeData = async () => {
      setInitialLoading(true);
      try {
        // Fetch types, sizes, and districts in parallel for better performance
        const promises = [fetchTypes(), fetchSizes(), fetchDistricts()];
        
        if (product_id) {
          setProductId(product_id);
          promises.push(fetchTileDetails());
        }
        
        await Promise.all(promises);
      } catch (error) {
        console.error("Error initializing data:", error);
        toast.error("Failed to load initial data. Please refresh the page.");
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, [product_id]);

  // Show loading state while initial data is being fetched
  if (initialLoading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">Loading tile data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">
              {showPdfUpload
                ? formData.manufacturing_company_name + " - Upload PDF"
                : product_id
                ? "Edit Tile Product"
                : "Create Tile Product"}
            </h4>
          </div>
          <div className="card-body">
            {!showPdfUpload ? (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Manufacturing Company Name</label>
                      <input
                        className="form-control"
                        value={formData.manufacturing_company_name}
                        onChange={(e) => handleChange("manufacturing_company_name", e.target.value)}
                      />
                    </div>
                  </div>
                
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Tile Grade *</label>
                      <Select
                        options={GRADE_CHOICES}
                        value={GRADE_CHOICES.find(
                          (opt) => opt.value === formData.tile_grade
                        )}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            tile_grade: selected?.value || "",
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Tile Type *</label>
                      <Select
                        options={types}
                        value={types.find(
                          (opt) => opt.value === formData.tile_type
                        )}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            tile_type: selected?.value || "",
                          }))
                        }
                        placeholder={typesLoading ? "Loading types..." : "Select tile type..."}
                        isLoading={typesLoading}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Tile Size *</label>
                      <Select
                        options={sizes}
                        value={sizes.find(
                          (opt) => opt.value === formData.tile_size
                        )}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            tile_size: selected?.value || "",
                          }))
                        }
                        placeholder={sizesLoading ? "Loading sizes..." : "Select tile size..."}
                        isLoading={sizesLoading}
                        required
                      />
                    </div>
                  </div>
                
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Box Weight (kg) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        name="box_weight_kg"
                        value={formData.box_weight_kg}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Thickness (mm) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        name="thickness_mm"
                        value={formData.thickness_mm}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Tile Body *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="tile_body"
                        value={formData.tile_body}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Company Name on Box *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="box"
                        value={formData.box}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Pieces per Box *</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        name="pcs_per_box"
                        value={formData.pcs_per_box}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Working Sqft per Box *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        name="working_sqft_per_box"
                        value={formData.working_sqft_per_box}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                
                  <div className="col-md-6">        
                    <div className="form-group">
                      <label>Loading Address *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="loading_address"
                        value={formData.loading_address}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Districts where you want to hide your stock</label>
                      <Select
                        isMulti
                        options={districts}
                        value={districts.filter(district => 
                          formData.hidden_district_ids.includes(district.value)
                        )}
                        onChange={(selected) => {
                          const ids = selected ? selected.map(item => item.value) : [];
                          const names = selected ? selected.map(item => item.label) : [];
                          setFormData((prev) => ({
                            ...prev,
                            hidden_district_ids: ids,
                            hidden_district_names: names,
                          }));
                        }}
                        placeholder={districtsLoading ? "Loading districts..." : "Select districts to hide..."}
                        isLoading={districtsLoading}
                        isClearable
                      />
                    </div>
                  </div>
 
                  <div className="col-md-12">        
                    <div className="form-group">
                      <label>Remark</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                      />
                    </div>
                  </div>
                </div>

                <div className="text-end">
                  <button
                    type="submit"
                    className="btn btn-primary btn-block me-2"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save & Continue"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => navigate("/products/tiles")}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <input
                      type="file"
                      multiple
                      className="form-control-file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      required
                    />
                  </div>
                  <small className="form-text text-muted">
                    Only PDF files are allowed. Maximum size: 10MB
                  </small>
                </div>
                <div className="col-md-4">
                  <label className="form-label mb-1">File Name</label>
                  <div className="input-group">
                    <span className="input-group-text">{defaultFileName}</span>
                    <input
                      type="text"
                      className="form-control"
                      name="fileName"
                      value={fileName}
                      placeholder="File Name"
                      onChange={(e) => setFileName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group text-end">
                    <label>&nbsp;</label>
                    <button
                      onClick={saveUpload}
                      className="btn btn-primary btn-block"
                      disabled={uploading || !selectedFile}
                    >
                      {uploading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TileCreate;
